// Minimal IndexedDB wrapper for the sync engine's device-local bookkeeping
// (the outbox + the pull cursor). Deliberately raw `indexedDB` — no extra npm
// dependency (assetto-gallery uses a tiny helper of its own too, see
// _planning/referencia-assetto.md §6.3). Browser-only; every export is
// SSR-safe (no-ops / rejects when `indexedDB` doesn't exist).

export const SYNC_OUTBOX_STORE = 'sync-outbox'
export const SYNC_META_STORE = 'sync-meta'

const DB_NAME = 'config-strike-sync'
const DB_VERSION = 1

let dbPromise = null

function hasIndexedDB() {
    return typeof indexedDB !== 'undefined'
}

function openDb() {
    if (!hasIndexedDB()) return Promise.reject(new Error('indexedDB not available'))
    if (dbPromise) return dbPromise
    dbPromise = new Promise((resolve, reject) => {
        const req = indexedDB.open(DB_NAME, DB_VERSION)
        req.onupgradeneeded = () => {
            const idb = req.result
            if (!idb.objectStoreNames.contains(SYNC_OUTBOX_STORE)) idb.createObjectStore(SYNC_OUTBOX_STORE)
            if (!idb.objectStoreNames.contains(SYNC_META_STORE)) idb.createObjectStore(SYNC_META_STORE)
        }
        req.onsuccess = () => resolve(req.result)
        req.onerror = () => reject(req.error)
    })
    return dbPromise
}

export async function getRecord(store, key) {
    const idb = await openDb()
    return new Promise((resolve, reject) => {
        const tx = idb.transaction(store, 'readonly')
        const req = tx.objectStore(store).get(key)
        req.onsuccess = () => resolve(req.result ?? null)
        req.onerror = () => reject(req.error)
    })
}

export async function putRecord(store, key, value) {
    const idb = await openDb()
    return new Promise((resolve, reject) => {
        const tx = idb.transaction(store, 'readwrite')
        tx.objectStore(store).put(value, key)
        tx.oncomplete = () => resolve()
        tx.onerror = () => reject(tx.error)
    })
}

/** Bulk put in a single transaction. `entries` is an array of [key, value]. */
export async function putRecords(store, entries) {
    const idb = await openDb()
    return new Promise((resolve, reject) => {
        const tx = idb.transaction(store, 'readwrite')
        const os = tx.objectStore(store)
        for (const [key, value] of entries) os.put(value, key)
        tx.oncomplete = () => resolve()
        tx.onerror = () => reject(tx.error)
    })
}

/** Returns all [key, value] pairs in the store. */
export async function getAllEntries(store) {
    const idb = await openDb()
    return new Promise((resolve, reject) => {
        const tx = idb.transaction(store, 'readonly')
        const os = tx.objectStore(store)
        const keysReq = os.getAllKeys()
        const valsReq = os.getAll()
        tx.oncomplete = () => resolve(keysReq.result.map((k, i) => [k, valsReq.result[i]]))
        tx.onerror = () => reject(tx.error)
    })
}

/**
 * Deletes each of `keys` only if `predicate(currentValue, key)` returns true —
 * one atomic transaction, so a concurrent write (markDirty) between the read
 * and the delete can never be silently lost.
 */
export async function deleteRecordsIf(store, keys, predicate) {
    if (keys.length === 0) return
    const idb = await openDb()
    return new Promise((resolve, reject) => {
        const tx = idb.transaction(store, 'readwrite')
        const os = tx.objectStore(store)
        for (const key of keys) {
            const req = os.get(key)
            req.onsuccess = () => {
                const current = req.result
                if (current !== undefined && predicate(current, key)) os.delete(key)
            }
        }
        tx.oncomplete = () => resolve()
        tx.onerror = () => reject(tx.error)
    })
}
